import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Users, Calendar, Utensils, Phone } from "lucide-react";
import { useLanguage } from "@/LanguageContext";
import { API } from "@/App";

const CateringPage = () => {
  const { lang, t } = useLanguage();
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMenus = async () => {
      try {
        const response = await axios.get(`${API}/catering-menus`);
        if (response.data && response.data.length > 0) {
          setMenus(response.data);
        }
      } catch (error) {
        console.error("Error fetching catering menus:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMenus();
  }, []);

  const getTitle = (menu) => lang === "en" && menu.title_en ? menu.title_en : menu.title;
  const getServes = (menu) => lang === "en" && menu.serves_en ? menu.serves_en : menu.serves;
  const getPrice = (menu) => lang === "en" && menu.price_en ? menu.price_en : menu.price;
  const getItems = (menu) => lang === "en" && menu.items_en ? menu.items_en : menu.items;

  return (
    <div className="min-h-screen" style={{ background: '#f4e7d8' }}>
      <Navbar />

      {/* Hero */}
      <div 
        className="py-20 mandala-pattern"
        style={{ 
          background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="indian-divider mb-4"
          >
            <span className="text-sm uppercase tracking-widest" style={{ color: '#d4af37' }}>Catering</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl font-serif-display font-semibold italic mb-4"
            style={{ color: '#d4af37' }}
            data-testid="catering-page-title"
          >
            {t("catering_title")}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={{ color: '#f5e6d3' }}
            className="text-lg max-w-3xl mx-auto"
          >
            {t("catering_subtitle")}
          </motion.p>
        </div>
      </div>

      {/* Decorative Divider */}
      <div className="h-2" style={{ background: 'linear-gradient(90deg, #721111, #d4af37, #721111)' }}></div>

      {/* Event Types */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="indian-divider mb-6">
              <span className="text-sm uppercase tracking-widest" style={{ color: '#8b4513' }}>Arrangementer</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-serif-display font-semibold italic" style={{ color: '#2d1810' }}>
              Perfekt for Enhver Anledning
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Users className="h-8 w-8" />, title: "Bryllup" },
              { icon: <Utensils className="h-8 w-8" />, title: "Bedriftsarrangementer" },
              { icon: <Calendar className="h-8 w-8" />, title: "Bursdagsfester" },
              { icon: <Phone className="h-8 w-8" />, title: "Private Arrangementer" },
            ].map((event, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 text-center dish-card"
                style={{ border: '2px solid #b27f47' }}
              >
                <div 
                  className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
                  style={{ background: '#721111' }}
                >
                  <span style={{ color: '#d4af37' }}>{event.icon}</span>
                </div>
                <h3 className="text-lg font-serif-display font-semibold" style={{ color: '#2d1810' }}>
                  {event.title}
                </h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Menus */}
      <section 
        className="py-20 mandala-pattern"
        style={{ background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="indian-divider mb-6">
              <span className="text-sm uppercase tracking-widest" style={{ color: '#d4af37' }}>Menyer</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-serif-display font-semibold italic" style={{ color: '#d4af37' }}>
              Cateringmenyer
            </h2>
            <p className="mt-2" style={{ color: '#f5e6d3' }}>Skreddersydd for ditt arrangement</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {loading ? (
              <p className="col-span-3 text-center py-12" style={{ color: '#f5e6d3' }}>
                {t("loading")}
              </p>
            ) : menus.length === 0 ? (
              <div className="col-span-3 text-center py-12">
                <p className="text-xl font-serif-display" style={{ color: '#d4af37' }}>
                  Kontakt oss for skreddersydde cateringmenyer
                </p>
                <p className="mt-3" style={{ color: '#f5e6d3' }}>
                  Vi lager menyen sammen med deg, tilpasset ditt arrangement og antall gjester.
                </p>
                <a href="tel:+4748915190" className="inline-block mt-6 px-8 py-3 rounded-full font-bold text-lg" style={{ background: '#d4af37', color: '#2d1810' }}>
                  Ring oss: +47 48 91 51 90
                </a>
              </div>
            ) : (
              menus.map((menu, index) => (
                <motion.div
                  key={menu.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6"
                  style={{ border: '3px solid #d4af37' }}
                  data-testid={`catering-menu-${index}`}
                >
                  <h3 className="text-2xl font-serif-display font-semibold mb-2" style={{ color: '#2d1810' }}>
                    {getTitle(menu)}
                  </h3>
                  <p className="text-sm mb-4" style={{ color: '#8b4513' }}>{getServes(menu)}</p>
                  <ul className="space-y-2 mb-6">
                    {getItems(menu).map((item, i) => (
                      <li key={i} className="text-sm flex items-start" style={{ color: '#4a3728' }}>
                        <span className="mr-2" style={{ color: '#d4af37' }}>•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <div className="pt-4" style={{ borderTop: '2px solid #d4af37' }}>
                    <p className="text-xl font-bold" style={{ color: '#721111' }}>{getPrice(menu)}</p>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Decorative Divider */}
      <div className="h-2" style={{ background: 'linear-gradient(90deg, #721111, #d4af37, #721111)' }}></div>

      {/* Contact CTA */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-serif-display font-semibold italic mb-4" style={{ color: '#2d1810' }}>
              Klar til å Planlegge Ditt Arrangement?
            </h2>
            <p className="mb-8 max-w-2xl mx-auto" style={{ color: '#4a3728' }}>
              Kontakt oss for tilpassede menyer og priser • Minimum 15 gjester
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="catering-contact-button">
                <Button className="btn-maroon rounded-full font-bold px-10 py-6 text-lg transition-transform hover:scale-105">
                  Kontakt Oss
                </Button>
              </Link>
              <a href="tel:+4748915190">
                <Button
                  className="rounded-full font-bold px-10 py-6 text-lg transition-transform hover:scale-105"
                  style={{ 
                    background: 'transparent', 
                    border: '2px solid #721111', 
                    color: '#721111' 
                  }}
                >
                  <Phone className="h-5 w-5 mr-2" />
                  Ring +47 48 91 51 90
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CateringPage;
